import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Fade, IconButton, styled, Tooltip} from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
// import setLeading = OPS.setLeading;
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import {useDropzone} from "react-dropzone";
import clsx from "clsx";
import GetAppIcon from "@mui/icons-material/GetAppOutlined";
import {
  ATHENA_GENDER_FEMALE,
  ATHENA_GENDER_MALE,
  AwaitableMergedPatientData,
  MergedPatientData,
  PATIENT_INFO_GENDER_FEMALE,
  PATIENT_INFO_GENDER_MALE,
  PatientDoorReportData,
  PatientInfoReportData,
} from "./patientVisitData";
import {readPdfTableLines} from "./pdfReader";
import {isMaccabiDoorReport, readMaccabiVisitReport,} from "./maccabiVisitReportParser";
import {isMaccabiPatientReport, mergeAllCells, readMaccabiPatientReport,} from "./maccabiPatientReportParser";
import {scrollableStyle} from "fe-common/ui/commonStyles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {useRouteMatch} from "react-router";
import {SendPatientEmailTable} from "./sendPatientEmailTable";
import {createClinicPatientCase} from "./createClinicPatientCase";
import {useClinicState} from "../../../../clinic/clinicStateContext";
import {SendCaseLinkMode, SendCaseLinksDialog} from "./sendCaseLinksDialog";
import {PatientViewTable} from "./patientViewTable";
import {isMaccabiDayReport} from "./maccabiDayReportParser";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailIcon from "@mui/icons-material/Mail";
import {ImportPatientDebug} from "./tabs/importPatientDebug";
import {KahunSliderTabs} from "fe-web-components/settings/presentaional/kahunSliderTabs";

const NO_DOOR_REPORT = "דוח דלת נקלט בהצלחה";
const DOOR_REPORT_PRESENT = "דוח דלת";

const NO_PATIENT_INFO_REPORT = "דוח תחקור פרטני  נקלט בהצלחה";
const PATIENT_INFO_PRESENT = "דוח תתקור פרטני";

//region [[ Styles ]]

const ImportPatientsView = styled("div")({
  display: "flex",
  flexFlow: "column",
  height: "100%",
  zIndex: 2,
  ...scrollableStyle,
  // direction: "rtl",
});

const UploadButton = styled("div")({
  color: "#8080808a",
  textAlign: "center",
  background: "#f7f7f7",
  border: "dashed 1px",
  padding: "14px",
  display: "flex",
  justifyContent: "space-around",
  flexDirection: "column",
  alignItems: "center",
  transition: "all 0.3s ease-in",
  height: "45px",
  cursor: "pointer",
  zIndex: 100,
  "& span": {
    display: "flex",
  },
});

const DisplayBoxes = styled("div")({
  width: "100%",
  minHeight: "100%",
  display: "flex",
  flexFlow: "row",
  flexWrap: "wrap",
  ...scrollableStyle,
});

const PageContent = styled("div")({
  height: "74%",
  width: "100%",
  cursor: "arrow",
});

const DropzoneArea = styled(UploadButton)({
  color: "white",
  opacity: 0,
  background: "transparent",
  border: "dashed 0px",
  zIndex: 0,
  position: "fixed",
  left: 0,
  top: 0,
  width: "100%",
  height: "100vh",
  cursor: "auto",
  "&.expanded": {
    zIndex: 1000,
    borderWidth: "3px",
    background: "#f7f7f7",
    border: "dashed 1px",
    color: "#8080808a",
    opacity: 1,
  },
  "&:focus": {
    outline: "none",
  },
  "&.disabled": {
    color: "#80808042",
    borderColor: "gainsboro",
    cursor: "not-allowed",
  },
});

const TopButtonArea = styled("div")({
  display: "flex",
  flexFlow: "row",
  zIndex: 200,
});

const FilesStatus = styled("div")({
  display: "flex",
  flexFlow: "column",
  margin: "10px 0",
});

const SendEmailsButton = styled(Button)({
  zIndex: 200,
  position: "relative",
  borderRadius: "14px",
  margin: "10px",
  backgroundColor: "#5A52C3",
  "&:hover, &:focused": {
    backgroundColor: "#651FAF",
  },
  "& span": {
    textTransform: "none",
    fontWeight: 700,
    color: "#E6E4FF",
  },
  "&:hover span": {
    color: "#232332",
  },
  "& svg": {
    fill: "#E6E4FF",
    margin: "0 5px",
  },
});

const SendWhatsAppButton = styled(SendEmailsButton)({
  backgroundColor: "#25db9d",
  "&:hover, &:focused": {
    backgroundColor: "#13dbb8",
  },
  "& span": {
    color: "white",
  },
  "& svg": {
    fill: "white",
  },
});

const FileText = styled("div")({
  display: "flex",
  flexFlow: "row",
  position: "relative",
  padding: "2px 29px",
  margin: "5px",
  alignItems: "center",
});

const CheckIconWrapper = styled("div")({
  "& svg": {
    position: "absolute",
    opacity: 1,
    left: 0,
    top: 0,
    transition: "all 0.5s ease-out",
  },
  "&.checked svg": {
    fill: "#25db9d",
    "& #checked": { opacity: 1 },
    "& #circle": { opacity: 0 },
  },
  "&.un-checked": {
    fill: "#232323",
    "& #checked": { opacity: 0 },
    "& #circle": { opacity: 1 },
  },
});

const ShowDebugText = styled("div")({
  width: "115.9px",
  zIndex: 1,
});

const DropzoneText = styled("span")({
  justifyContent: "center",
});

const DropIcon = styled(GetAppIcon)({ marginLeft: "8px" });

const DebugButton = styled("div")({
  fontWeight: 700,
  display: "flex",
  flexDirection: "row-reverse",
  alignItems: "center",
  margin: "10px 0",
  zIndex: 1,
});

const DebugIcon = styled(IconButton)({
  width: "32px",
  height: "32px",
  padding: 0,
  marginRight: "5px",
  background: "pink",
  "&.general": {
    backgroundColor: "paleturquoise",
  },
  "& .MuiIconButton-label": {
    position: "relative",
    width: "15px",
    height: "8px",
    top: 0,
    left: 0,
  },
  "&  span.dash-1, &  span.dash-2": {
    position: "absolute",
    transition: "all 0.7s ease-out",
    top: "-28px",
    left: 0,
    color: "#232323",
    fontSize: "2em",
  },
  "&.plus": {
    "& span.dash-2": {
      transform: "rotate(90deg)",
      top: "-24px",
      left: "4px",
    },
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]
const flipDates = (date: string) => {
  // translates DD/MM/YYYY to MM/DD/YYYY
  const split = date.split("/");
  return `${split[1]}/${split[0]}/${split[2]}`;
};

const mergePatientInfo = (...lists: PatientInfoReportData[][]) => {
  const id2Data: Record<string, PatientInfoReportData> = {};
  lists.forEach((list) => list.forEach((pInfo) => (id2Data[pInfo.id] = pInfo)));
  return Object.values(id2Data);
};
export interface ImportPatientsProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

// Only way i managed to to make the pdfjslib run was using an older version based on this discussion
// https://github.com/ansu5555/pdf-viewer-reactjs/issues/78
export const ImportPatient = ({ ...props }: ImportPatientsProps) => {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const queryParams = useRef(new URLSearchParams(window?.location?.search));

  const [
    {
      clinicData: { id: clinicId },
    },
  ] = useClinicState();

  const [loading, setLoading] = useState(false);

  const [isDebugOpen, setIsDebugOpen] = useState(
    queryParams?.current?.has("debug"),
  );

  const [isMergedDataShown, setShowMergedData] = useState(true);

  const [displayText, setDisplayText] = useState<React.ReactNode>([]);

  const [patientVisitData, setPatientVisitData] = useState<
    PatientDoorReportData[]
  >([]);

  const [patientInfo, setPatientInfo] = useState<PatientInfoReportData[]>([]);

  const [mergedData, setMergedData] = useState<AwaitableMergedPatientData[]>(
    [],
  );

  const dropZoneRef = useRef(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [showSendEmailPopup, setShowSendEmailPopup] = useState(false);

  const [showSendWhatsappPopup, setShowSendWhatsAppPopup] = useState(false);

  const handleIdSelected = useCallback(
    (p: MergedPatientData, checked: boolean) => {
      if (!checked) {
        setSelectedIds((old) => {
          let ids = old;
          let index = ids.findIndex((oIds) => oIds === p.id);
          if (index < 0) {
            // If we remove an item which isn't in the list,
            if (ids?.length) return old; // If there is a list, don't affect it
            // otherwise the list is empty, so make sure it holds all the items, and make everyone selected beside this id
            ids = mergedData.map((md) => md.id);
            index = ids.findIndex((oIds) => oIds === p.id);
            if (index < 0) return old; //safty check - if this id isn't in the list even don't do anything
          }
          return [...ids.slice(0, index), ...ids.slice(index + 1)];
        });
      } else {
        setSelectedIds((old) => [...old, p.id]);
      }
    },
    [setSelectedIds, mergedData],
  );

  const addMissingPatientInfo = useCallback(
    (newInfo: PatientInfoReportData) => {
      // convert to PatientReportData
      const prd = Object.entries(newInfo).reduce(
        (acc, [currentKey, currentValue]) => ({
          ...acc,
          [currentKey]:
            currentKey === "age" ? parseInt(currentValue.toString()) : currentValue,
        }),
        {},
      ) as PatientInfoReportData;
      setPatientInfo((older) => {
        // if exisitng, update
        const index = older.findIndex((i) => i.id === newInfo.id);
        if (index >= 0) {
          return [...older.slice(0, index), newInfo, ...older.slice(index + 1)];
        }
        // else add
        return [...older, prd];
      });
    },
    [setPatientInfo],
  );

  const getMergedData = useCallback(
    () =>
      patientInfo.reduce(
        (acc: AwaitableMergedPatientData[], currentPatientInfo) => {
          const visitData = patientVisitData.find(
            (pv) =>
              currentPatientInfo?.id?.trim?.().endsWith?.(pv.partialId) &&
              // Some might have the same 4 letters ending so match by name also
              (currentPatientInfo?.firstName.includes(pv.name) ||
                // since some have long double names, if we couldn't find by the long name, try to match by only the 2 first letters
                currentPatientInfo?.firstName.includes(
                  pv.name.substring(0, 2),
                )),
          );

          if (!visitData) {
            console.log(
              `Couldn't find visit data for ${currentPatientInfo.firstName} ${currentPatientInfo.lastName}`,
              currentPatientInfo,
            );
            return acc;
          }

          const partialPatientData: MergedPatientData = {
            id: currentPatientInfo.id,
            index: visitData.index,
            gender:
              currentPatientInfo.gender === PATIENT_INFO_GENDER_MALE
                ? ATHENA_GENDER_MALE
                : currentPatientInfo.gender === PATIENT_INFO_GENDER_FEMALE
                ? ATHENA_GENDER_FEMALE
                : null,
            age: currentPatientInfo.age,
            firstName: currentPatientInfo.firstName,
            lastName: currentPatientInfo.lastName,
            visitDateTime: new Date(
              `${flipDates(visitData.visitDate)} ${visitData.visitTime}`,
            ).getTime(),
            email: currentPatientInfo.email,
            phoneNumber: currentPatientInfo.phoneNumber,
          };

          return [
            ...acc,
            {
              ...partialPatientData,
              urlPromise: createClinicPatientCase(clinicId, partialPatientData),
            },
          ];
        },
        [],
      ),
    [patientInfo, patientVisitData, clinicId],
  );

  // useEffect(() => {
  //   const fillVisitData = async () => {
  //     if (mergedData?.length && mergedData?.some((md) => !md.caseUrl)) {
  //       const newMergedData = await Promise.all(
  //         mergedData.map(async (md) => {
  //           if (md.caseUrl) return md;
  //           return {
  //             ...md,
  //             caseUrl: await md.urlPromise,
  //           };
  //         }),
  //       );
  //       setMergedData(newMergedData);
  //     }
  //   };
  //   fillVisitData();
  // }, [mergedData]);

  const [files, setFiles] = useState(null);

  const onPdfDropped = useCallback(
    (files) => {
      setLoading(true);
      setFiles(files);
    },
    [setLoading, setFiles],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: onPdfDropped,
  });

  const match = useRouteMatch();

  // TODO: move to pdfParser
  const paintPdfParserDebugData = useCallback(
    (file) => {
      (async function() {
        const fileContent = await new Promise((resolve, reject) => {
          try {
            console.log(file);
            const fileReader = new FileReader();
            fileReader.onloadend = (e) => resolve(fileReader.result);
            fileReader.onerror = (e) => reject(e);
            fileReader.readAsArrayBuffer(file);
          } catch (e) {
            reject(e);
          }
        });
        console.log("readArrayBuffer:", fileContent);
        const pdf = await pdfjsLib.getDocument(fileContent).promise;

        console.log("Read pdf!", pdf);

        const maxPages = pdf.numPages;

        console.log("pdf maxPages", maxPages);

        let countPromises = []; // collecting all page promises

        for (let j = 1; j <= maxPages; j++) {
          const page = pdf.getPage(j);
          countPromises.push(
            page.then(function(page) {
              // add page promise
              const textContent = page.getTextContent();
              return textContent.then(function(text) {
                let lastTransformYIndex = null;
                let prevXPos = 0;
                // return content promise
                return text.items.map(function(s, index) {
                  // if (j === 1 && index < 17) return "";
                  const curentYpos = s.transform.slice(-1).pop();
                  const currentXPos = s.transform.slice(-2)[0];

                  if (!lastTransformYIndex && lastTransformYIndex !== 0)
                    lastTransformYIndex = curentYpos;
                  else if (
                    Math.abs(lastTransformYIndex - curentYpos) >
                      10 /*SAME_LINE_THRESHOLD*/ &&
                    prevXPos &&
                    Math.abs(currentXPos - prevXPos) > 100
                  ) {
                    prevXPos = currentXPos;
                    // New line
                    lastTransformYIndex = curentYpos;
                    return (
                      <Tooltip
                        title={JSON.stringify(
                          { t: s.transform, w: s.width },
                          null,
                          4,
                        )}
                      >
                        <span>
                          <br />
                          {s.str + " "}
                        </span>
                      </Tooltip>
                    );
                  }
                  prevXPos = currentXPos;
                  return (
                    <Tooltip
                      title={JSON.stringify(
                        { t: s.transform, w: s.width },
                        null,
                        4,
                      )}
                    >
                      <span>{s.str + " "}</span>
                    </Tooltip>
                  );
                  // return `{index: ${String(index).padStart(2)}, ${s.str}}\r\n`;
                  // return JSON.stringify(s, null, 4);
                });
              });
            }),
          );
        }

        const texts = await Promise.all(countPromises);

        setDisplayText((old) => [old, texts.map((t) => <p>{t}</p>)]);
      })();
    },
    [setDisplayText],
  );

  useEffect(() => {
    const readPdf = async function(file) {
      setLoading(true);
      const lines = await readPdfTableLines(file);
      if (isMaccabiDoorReport(lines)) {
        setPatientVisitData(readMaccabiVisitReport(lines));
      }
      if (isMaccabiPatientReport(lines)) {
        setPatientInfo((olderPatientInfos) =>
          mergePatientInfo(olderPatientInfos, readMaccabiPatientReport(lines)),
        );
      }
      if (isMaccabiDayReport(lines)) {
        setDisplayText([
          lines.map((l, index) => (
            <p dir={"rtl"} key={index}>
              {index < 2
                ? mergeAllCells(l)
                : Array.isArray(l)
                ? l.map((word, i) => <p key={i}>{mergeAllCells(word)}</p>)
                : l}
            </p>
          )),
        ]);
      }
      if (
        queryParams?.current?.get("debug") === "rows" ||
        queryParams?.current?.get("debug") === "all"
      ) {
        setDisplayText([
          lines.map((l, index) => (
            <p key={index}>{JSON.stringify(l, null, 2)}</p>
          )),
        ]);
      }
      setLoading(false);
    };
    const readJson = async function readJson(file) {
      const fileContent = (await new Promise((resolve, reject) => {
        try {
          const fileReader = new FileReader();
          fileReader.onloadend = (e) => resolve(fileReader.result);
          fileReader.onerror = (e) => reject(e);
          fileReader.readAsText(file);
        } catch (e) {
          reject(e);
        }
      }));
      try {
        const obj = JSON.parse(fileContent as string);
        if (obj?.objectDescription === "patientInfo") {
          setPatientInfo(obj.data);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (files && files?.length) {
      files.forEach((f) => {
        if (f.type === "application/pdf")
          readPdf(f).finally(() => setLoading(false));
        else if (f.type === "application/json")
          readJson(f).finally(() => setLoading(false));
        if (queryParams?.current?.has("debug")) paintPdfParserDebugData(f);
      });
    }
  }, [setLoading, setDisplayText, files, match, paintPdfParserDebugData]);

  //TODO:
  useEffect(() => {
    setMergedData(getMergedData);
    setShowMergedData(true);
  }, [setMergedData, getMergedData, setShowMergedData]);

  useEffect(() => {
    if (isDragActive) {
      dropZoneRef?.current?.scrollIntoView?.();
    }
  }, [isDragActive]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const importPatientTabs = useMemo(
    () => [
      {
        key: "merged-data",
        name: "Merged data",
        tooltip: "מידע מאוחד מוכן לשליחה",
        component: (
          <SendPatientEmailTable
            patients={mergedData}
            onIdSelectChange={handleIdSelected}
            selectedIds={selectedIds}
          />
        ),
      },
      {
        key: "patient-visit-data",
        name: "Visit Data",
        tooltip: "דוח דלת מנותח",
        component: (
          <PatientViewTable
            data={patientVisitData}
            objectDescription={"patientVisitData"}
          />
        ),
      },
      {
        key: "patient-information",
        name: "Patients Database",
        tooltip: "רשימת מטופלים",
        component: (
          <PatientViewTable
            data={patientInfo}
            onAddNewData={addMissingPatientInfo}
            objectDescription={"patientInfo"}
          />
        ),
      },
      {
        key: "debug",
        name: "🪳Debug",
        tooltip: "מידע כללי על פינוח הקריאה",
        component: (
          <ImportPatientDebug
            isDebugOpen={isDebugOpen}
            patientVisitData={patientVisitData}
            patientInfo={patientInfo}
            displayText={displayText}
          />
        ),
      },
    ],
    [
      handleIdSelected,
      isDebugOpen,
      patientVisitData,
      patientInfo,
      displayText,
      addMissingPatientInfo,
      mergedData,
      selectedIds,
    ],
  );

  return (
    <ImportPatientsView>
      {loading && "loading"}
      <TopButtonArea className={"top-button-area"}>
        <FilesStatus>
          <FileText>
            <CheckIconWrapper
              className={clsx(
                patientVisitData?.length ? "checked" : "un-checked",
              )}
            >
              <CheckCircleOutlineIcon id={"checked"} />
              <RadioButtonUncheckedIcon id={"circle"} />
            </CheckIconWrapper>
            {patientVisitData.length ? NO_DOOR_REPORT : DOOR_REPORT_PRESENT}
          </FileText>
          <FileText>
            <CheckIconWrapper
              className={patientInfo?.length ? "checked" : "un-checked"}
            >
              <CheckCircleOutlineIcon id={"checked"} />
              <RadioButtonUncheckedIcon id={"circle"} />
            </CheckIconWrapper>
            {patientInfo.length ? NO_PATIENT_INFO_REPORT : PATIENT_INFO_PRESENT}
          </FileText>
        </FilesStatus>
        <Fade
          in={!!patientInfo.length && !!patientVisitData.length}
          style={{ zIndex: 200 }}
          mountOnEnter
          unmountOnExit
        >
          <SendEmailsButton onClick={() => setShowSendEmailPopup(true)}>
            <MailIcon />
            שלח אימייל
          </SendEmailsButton>
        </Fade>
        <Fade
          in={!!patientInfo.length && !!patientVisitData.length}
          style={{ zIndex: 200 }}
          mountOnEnter
          unmountOnExit
        >
          <SendWhatsAppButton onClick={() => setShowSendWhatsAppPopup(true)}>
            <WhatsAppIcon />
            שלח הודעות
          </SendWhatsAppButton>
        </Fade>
        <SendCaseLinksDialog
          mode={
            showSendWhatsappPopup
              ? SendCaseLinkMode.Whatsapp
              : SendCaseLinkMode.Email
          }
          open={showSendEmailPopup || showSendWhatsappPopup}
          onClose={() =>
            showSendWhatsappPopup
              ? setShowSendWhatsAppPopup(false)
              : setShowSendEmailPopup(false)
          }
          patientInfo={mergedData}
          selectedIds={selectedIds}
          onItemRemoved={(pInfo) => handleIdSelected(pInfo, false)}
        />
      </TopButtonArea>
      <UploadButton onClick={() => open()}>
        <input {...getInputProps()} />
        <DropzoneText>
          גרור לפה דוח דלת/דוח תחקור/רשימת מטופלים שמורה, או לחץ כאן כדי להעלות
          קובץ
          <DropIcon />
        </DropzoneText>
      </UploadButton>
      <DropzoneArea
        className={clsx(
          loading ? "disabled" : "",
          isDragActive ? "expanded" : "",
        )}
        ref={dropZoneRef}
        {...getRootProps({
          onClick: (event) => event.stopPropagation(),
        })}
      >
        <DropzoneText>
          גרור לפה דוח דלת/דוח תחקור/רשימת מטופלים שמורה
          <DropIcon />
        </DropzoneText>
      </DropzoneArea>
      <PageContent>
        <KahunSliderTabs
          tabs={importPatientTabs}
          selectedIndex={selectedIndex}
          onTabSelected={(_, index) => setSelectedIndex(index)}
        />
      </PageContent>
    </ImportPatientsView>
  );
};
